import type { ComponentType, ReactNode } from 'react';
import type { LucideProps } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { cn } from '../lib/cn.js';
import { Badge } from './Badge.js';

export interface SidebarItem {
  label: string;
  to: string;
  icon: ComponentType<LucideProps>;
  badge?: ReactNode;
  end?: boolean;
}

export interface SidebarSection {
  title?: string;
  items: SidebarItem[];
}

export interface SidebarProps {
  sections: SidebarSection[];
  className?: string;
  footer?: ReactNode;
}

export function Sidebar({ sections, className, footer }: SidebarProps) {
  return (
    <nav
      aria-label="Sidebar navigation"
      className={cn(
        'flex h-full w-60 shrink-0 flex-col border-r border-border-subtle bg-bg-surface-1',
        className,
      )}
    >
      <div className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-6">
        {sections.map((section, si) => (
          <div key={section.title ?? `section-${si}`} className="flex flex-col gap-1">
            {section.title && (
              <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                {section.title}
              </div>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 rounded-md px-3 py-2 text-base text-text-secondary transition-colors',
                    'hover:bg-bg-surface-2 hover:text-text-primary',
                    isActive && 'bg-bg-surface-3 text-text-primary font-medium',
                  )
                }
              >
                <item.icon className="size-5 shrink-0" aria-hidden />
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge && (typeof item.badge === 'string' ? <Badge variant="neutral">{item.badge}</Badge> : item.badge)}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
      {footer && <div className="border-t border-border-subtle px-3 py-3">{footer}</div>}
    </nav>
  );
}
